import { useAuth } from '../../auth';
import { useLocalParticipant, useTracks } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRoom } from '../room/RoomProvider';
import {
  appendSourceRecordingChunk,
  createSourceRecordingSegment,
  getSourceRecordingDedupeKey,
  markSourceRecordingSegmentStopped,
  uploadQueuedSourceRecordings,
} from './SourceRecordingUploadQueue';
import { getCameraDeviceKey, getCameraLabel, getStoredCameraDeviceLabel } from './cameraDeviceLabels';
import { SOURCE_RECORDING_PROFILE, getCoveredDrawRect, getSourceRecordingCanvasSize } from './sourceRecordingProfile';
import { isActiveModelCameraParticipant } from './liveKitCameraProfiles';
import { logVideoDiagnostic } from './videoDiagnostics';

const CHUNK_INTERVAL_MS = 5000;
const CAMERA_SWITCH_SETTLE_MS = 1000;
const SOURCE_VIDEO_READY_TIMEOUT_MS = 1500;

const MIME_TYPE_CANDIDATES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8',
  'video/webm',
  'video/mp4;codecs=h264,aac',
  'video/mp4;codecs=h264',
];

interface TrackInfo {
  cameraId: string;
  cameraLabel: string;
  sourceId: string;
  mediaStreamTrack: MediaStreamTrack;
}

interface PoseInfo {
  key: string;
  poseName: string | null;
  poseIndex: number | null;
}

interface ActiveSegment {
  uploadId: string;
  cameraId: string;
  cameraLabel: string;
  sourceId: string;
  dedupeKey: string;
  pose: PoseInfo;
  recorder: MediaRecorder;
  contentType: string;
  chunkCount: number;
  startedAt: string;
  hasAudio: boolean;
  stopRecordingSource: () => void;
  stopped: Promise<void>;
  resolveStopped: () => void;
  pendingStore: Promise<void>;
  isStopping: boolean;
}

interface RecordingSource {
  mediaStream: MediaStream;
  canvasWidth: number;
  canvasHeight: number;
  sourceWidth: number;
  sourceHeight: number;
  stop: () => void;
}

function browserSourceRecorderEnabled() {
  if (import.meta.env.VITE_BROWSER_SOURCE_RECORDING_ENABLED === 'false') return false;
  return window.localStorage.getItem('browser-source-recorder') !== 'off';
}

function getSupportedMimeType() {
  if (typeof MediaRecorder === 'undefined') return null;
  return MIME_TYPE_CANDIDATES.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) ?? '';
}

function createUploadId() {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function isUsableChunk(blob: Blob) {
  return blob.size > 0;
}

function waitForSourceVideo(video: HTMLVideoElement): Promise<void> {
  if (video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      video.removeEventListener('loadedmetadata', finish);
      video.removeEventListener('canplay', finish);
      resolve();
    };
    const timeout = window.setTimeout(finish, SOURCE_VIDEO_READY_TIMEOUT_MS);
    video.addEventListener('loadedmetadata', finish);
    video.addEventListener('canplay', finish);
  });
}

function drawSourceRecordingFrame(
  context: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  sourceTrack: MediaStreamTrack
) {
  const sourceSettings = sourceTrack.getSettings();
  const targetWidth = context.canvas.width;
  const targetHeight = context.canvas.height;
  const sourceWidth = video.videoWidth || sourceSettings.width || targetWidth;
  const sourceHeight = video.videoHeight || sourceSettings.height || targetHeight;
  const rect = getCoveredDrawRect({
    sourceWidth,
    sourceHeight,
    targetWidth,
    targetHeight,
  });

  context.fillStyle = '#111';
  context.fillRect(0, 0, targetWidth, targetHeight);

  if (video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
    context.drawImage(video, rect.x, rect.y, rect.width, rect.height);
  }
}

async function createRecordingSource(
  videoTrack: MediaStreamTrack,
  audioTrack: MediaStreamTrack | null
): Promise<RecordingSource> {
  const sourceVideo = document.createElement('video');
  sourceVideo.muted = true;
  sourceVideo.playsInline = true;
  sourceVideo.srcObject = new MediaStream([videoTrack]);

  await sourceVideo.play().catch(() => undefined);
  await waitForSourceVideo(sourceVideo);
  const sourceSettings = videoTrack.getSettings();
  const sourceWidth = sourceVideo.videoWidth || sourceSettings.width || SOURCE_RECORDING_PROFILE.width;
  const sourceHeight = sourceVideo.videoHeight || sourceSettings.height || SOURCE_RECORDING_PROFILE.height;
  const canvasSize = getSourceRecordingCanvasSize({ sourceWidth, sourceHeight });
  const canvas = document.createElement('canvas');
  if (!canvas.captureStream) {
    throw new Error('Canvas source recording is not available in this browser.');
  }

  const profile = SOURCE_RECORDING_PROFILE;
  canvas.width = canvasSize.width;
  canvas.height = canvasSize.height;
  const context = canvas.getContext('2d', { alpha: false });
  if (!context) {
    throw new Error('Could not prepare source recording canvas.');
  }

  drawSourceRecordingFrame(context, sourceVideo, videoTrack);

  const frameInterval = window.setInterval(() => {
    drawSourceRecordingFrame(context, sourceVideo, videoTrack);
  }, 1000 / profile.frameRate);
  const canvasStream = canvas.captureStream(profile.frameRate);
  const mediaStream = new MediaStream([
    ...canvasStream.getVideoTracks(),
    ...(audioTrack ? [audioTrack] : []),
  ]);
  let stopped = false;

  return {
    mediaStream,
    canvasWidth: canvas.width,
    canvasHeight: canvas.height,
    sourceWidth,
    sourceHeight,
    stop: () => {
      if (stopped) return;
      stopped = true;
      window.clearInterval(frameInterval);
      canvasStream.getTracks().forEach((track) => track.stop());
      sourceVideo.pause();
      sourceVideo.srcObject = null;
    },
  };
}

export function BrowserSourceRecorder({ roomCode }: { roomCode: string }) {
  const { getToken, isSignedIn } = useAuth();
  const { localParticipant } = useLocalParticipant();
  const tracks = useTracks([Track.Source.Camera, Track.Source.Microphone], { onlySubscribed: false });
  const { modelParticipantIds, poseTimer, sessionState } = useRoom();
  const segmentsRef = useRef<Map<string, ActiveSegment>>(new Map());
  const startingCameraIdsRef = useRef<Set<string>>(new Set());
  const getTokenRef = useRef(getToken);
  const uploadInProgressRef = useRef(false);
  const [cameraSwitching, setCameraSwitching] = useState(false);
  const [cameraTrackRefreshKey, setCameraTrackRefreshKey] = useState(0);

  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  const enabled = browserSourceRecorderEnabled();
  const localParticipantProfileId = useMemo(() => (
    localParticipant?.identity
      ? sessionState?.participants.find(participant => participant.participantId === localParticipant.identity)?.profileId ??
        sessionStorage.getItem(`participant-profileId-${roomCode}`)
      : null
  ), [localParticipant?.identity, roomCode, sessionState?.participants]);
  const isLocalModel = isActiveModelCameraParticipant({
    participantId: localParticipant?.identity,
    participantProfileId: localParticipantProfileId,
    modelParticipantId: sessionState?.modelParticipantId,
    modelProfileId: sessionState?.modelProfileId,
    modelParticipantIds,
  });
  const diagnosticContext = useMemo(() => ({
    roomCode,
    participantId: localParticipant?.identity ?? null,
    participantName: localParticipant?.name ?? null,
    participantRole: sessionStorage.getItem(`participant-role-${roomCode}`),
  }), [localParticipant?.identity, localParticipant?.name, roomCode]);
  const safeToUpload = !poseTimer?.isActive || poseTimer.isBreak || sessionState?.status === 'ended';
  const safeToUploadRef = useRef(safeToUpload);
  safeToUploadRef.current = safeToUpload;
  const activePose = useMemo<PoseInfo | null>(() => {
    if (!poseTimer?.isActive || poseTimer.isBreak) return null;
    return {
      key: [
        poseTimer.startedAt ?? 'session',
        poseTimer.currentPose,
        poseTimer.poseName || 'pose',
      ].join(':'),
      poseName: poseTimer.poseName || null,
      poseIndex: Number.isFinite(poseTimer.currentPose) ? poseTimer.currentPose : null,
    };
  }, [poseTimer?.currentPose, poseTimer?.isActive, poseTimer?.isBreak, poseTimer?.poseName, poseTimer?.startedAt]);

  const localModelTracks = useMemo<TrackInfo[]>(() => {
    if (!isLocalModel || !localParticipant?.identity) return [];

    return tracks
      .filter((trackRef) =>
        trackRef.source === Track.Source.Camera &&
        trackRef.participant.identity === localParticipant.identity
      )
      .map((trackRef, index) => {
        const mediaStreamTrack = trackRef.publication?.track?.mediaStreamTrack ?? null;
        if (!mediaStreamTrack || mediaStreamTrack.readyState !== 'live') return null;
        const sourceId = mediaStreamTrack.id || trackRef.publication?.trackSid || 'local-camera';
        const fallbackCameraId = sourceId;
        const cameraDeviceKey = getCameraDeviceKey(mediaStreamTrack.getSettings(), fallbackCameraId);

        return {
          cameraId: cameraDeviceKey,
          cameraLabel: getStoredCameraDeviceLabel(roomCode, cameraDeviceKey, index) || getCameraLabel(index),
          sourceId,
          mediaStreamTrack,
        };
      })
      .filter((track): track is TrackInfo => track !== null);
  }, [cameraTrackRefreshKey, isLocalModel, localParticipant?.identity, roomCode, tracks]);

  const localMicrophoneTrack = useMemo(() => {
    const trackedMicrophone = tracks.find((trackRef) =>
      trackRef.source === Track.Source.Microphone &&
      trackRef.participant.identity === localParticipant?.identity
    )?.publication?.track?.mediaStreamTrack ?? null;
    const participantMicrophone = localParticipant
      ?.getTrackPublication(Track.Source.Microphone)
      ?.track
      ?.mediaStreamTrack ?? null;
    const mediaStreamTrack = trackedMicrophone ?? participantMicrophone;

    if (!mediaStreamTrack || mediaStreamTrack.readyState !== 'live') return null;
    return mediaStreamTrack;
  }, [localParticipant, localParticipant?.identity, tracks]);

  const uploadStoredSegments = useCallback(async (uploadPhase: 'break' | 'session_end') => {
    if (uploadInProgressRef.current || !safeToUploadRef.current) return;
    uploadInProgressRef.current = true;

    try {
      await uploadQueuedSourceRecordings({
        roomCode,
        uploadPhase,
        getToken: (options) => getTokenRef.current(options),
        shouldUpload: () => safeToUploadRef.current,
        finalizeOpenSegments: uploadPhase === 'session_end',
      });
    } catch (error) {
      console.error('[BrowserSourceRecorder] Stored source recording upload failed', {
        roomCode,
        uploadPhase,
        error,
      });
    } finally {
      uploadInProgressRef.current = false;
    }
  }, [roomCode]);

  const stopSegment = useCallback(async (cameraId: string, reason: string) => {
    const segment = segmentsRef.current.get(cameraId);
    if (!segment || segment.isStopping) return;

    segment.isStopping = true;
    segmentsRef.current.delete(cameraId);
    const stoppedAt = Date.now();

    try {
      if (segment.recorder.state !== 'inactive') {
        segment.recorder.requestData();
        segment.recorder.stop();
      }

      await segment.stopped;
      await segment.pendingStore;
      await markSourceRecordingSegmentStopped({
        uploadId: segment.uploadId,
        stoppedAt,
      });

      if (safeToUploadRef.current) {
        void uploadStoredSegments(sessionState?.status === 'ended' ? 'session_end' : 'break');
      } else {
        console.log('[BrowserSourceRecorder] Source recording persisted until break', {
          reason,
          uploadId: segment.uploadId,
          cameraId: segment.cameraId,
          cameraLabel: segment.cameraLabel,
          pose: segment.pose,
          chunkCount: segment.chunkCount,
        });
      }
      logVideoDiagnostic('info', 'source-recording-segment-stopped', {
        reason,
        uploadId: segment.uploadId,
        cameraId: segment.cameraId,
        cameraLabel: segment.cameraLabel,
        sourceId: segment.sourceId,
        pose: segment.pose,
        chunkCount: segment.chunkCount,
        safeToUpload: safeToUploadRef.current,
      }, diagnosticContext);
    } catch (error) {
      console.error('[BrowserSourceRecorder] Failed to stop source recording', {
        reason,
        uploadId: segment.uploadId,
        cameraId: segment.cameraId,
        error,
      });
      logVideoDiagnostic('error', 'source-recording-segment-stop-failed', {
        reason,
        uploadId: segment.uploadId,
        cameraId: segment.cameraId,
        error,
      }, diagnosticContext);
    }
  }, [diagnosticContext, sessionState?.status, uploadStoredSegments]);

  useEffect(() => {
    let settleTimer: number | null = null;

    const refreshCameraTracks = () => {
      setCameraTrackRefreshKey((key) => key + 1);
    };

    const handleSwitchStarted = () => {
      if (settleTimer !== null) {
        window.clearTimeout(settleTimer);
        settleTimer = null;
      }

      setCameraSwitching(true);
      refreshCameraTracks();
      Array.from(segmentsRef.current.keys()).forEach((cameraId) => {
        void stopSegment(cameraId, 'camera-switch-started');
      });
    };

    const handleSwitchFinished = () => {
      refreshCameraTracks();
      if (settleTimer !== null) {
        window.clearTimeout(settleTimer);
      }

      settleTimer = window.setTimeout(() => {
        setCameraSwitching(false);
        refreshCameraTracks();
        settleTimer = null;
      }, CAMERA_SWITCH_SETTLE_MS);
    };

    window.addEventListener('drawclub:camera-device-switch-started', handleSwitchStarted);
    window.addEventListener('drawclub:camera-device-switch-finished', handleSwitchFinished);
    window.addEventListener('drawclub:camera-device-track-refreshed', refreshCameraTracks);

    return () => {
      window.removeEventListener('drawclub:camera-device-switch-started', handleSwitchStarted);
      window.removeEventListener('drawclub:camera-device-switch-finished', handleSwitchFinished);
      window.removeEventListener('drawclub:camera-device-track-refreshed', refreshCameraTracks);
      if (settleTimer !== null) {
        window.clearTimeout(settleTimer);
      }
    };
  }, [stopSegment]);

  const startSegment = useCallback(async (track: TrackInfo, pose: PoseInfo) => {
    if (
      !enabled ||
      !isSignedIn ||
      segmentsRef.current.has(track.cameraId) ||
      startingCameraIdsRef.current.has(track.cameraId)
    ) {
      return;
    }
    startingCameraIdsRef.current.add(track.cameraId);

    const mimeType = getSupportedMimeType();
    if (mimeType === null) {
      console.warn('[BrowserSourceRecorder] MediaRecorder is unavailable in this browser');
      startingCameraIdsRef.current.delete(track.cameraId);
      return;
    }

    let recordingSource: RecordingSource | null = null;

    try {
      const uploadId = createUploadId();
      const startedAt = new Date().toISOString();
      const dedupeKey = getSourceRecordingDedupeKey({
        roomCode,
        poseKey: pose.key,
        cameraId: track.cameraId,
        sourceId: track.sourceId,
      });
      recordingSource = await createRecordingSource(track.mediaStreamTrack, localMicrophoneTrack);
      const recorder = new MediaRecorder(recordingSource.mediaStream, {
        ...(mimeType ? { mimeType } : {}),
        videoBitsPerSecond: SOURCE_RECORDING_PROFILE.videoBitsPerSecond,
        ...(localMicrophoneTrack ? { audioBitsPerSecond: SOURCE_RECORDING_PROFILE.audioBitsPerSecond } : {}),
      });
      const queuedSegment = await createSourceRecordingSegment({
        roomCode,
        uploadId,
        dedupeKey,
        cameraId: track.cameraId,
        cameraLabel: track.cameraLabel,
        poseKey: pose.key,
        poseName: pose.poseName,
        poseIndex: pose.poseIndex,
        contentType: recorder.mimeType || mimeType || 'video/webm',
        startedAt,
      });

      if (!queuedSegment.created) {
        recordingSource.stop();
        console.log('[BrowserSourceRecorder] Source recording already exists for this pose and camera', {
          roomCode,
          uploadId: queuedSegment.segment.uploadId,
          dedupeKey,
          cameraId: track.cameraId,
          cameraLabel: track.cameraLabel,
          pose,
        });
        startingCameraIdsRef.current.delete(track.cameraId);
        return;
      }

      let resolveStopped: () => void = () => {};
      const stopped = new Promise<void>((resolve) => {
        resolveStopped = resolve;
      });

      const segment: ActiveSegment = {
        uploadId,
        cameraId: track.cameraId,
        cameraLabel: track.cameraLabel,
        sourceId: track.sourceId,
        dedupeKey,
        pose,
        recorder,
        contentType: recorder.mimeType || mimeType || 'video/webm',
        chunkCount: 0,
        startedAt,
        hasAudio: Boolean(localMicrophoneTrack),
        stopRecordingSource: recordingSource.stop,
        stopped,
        resolveStopped,
        pendingStore: Promise.resolve(),
        isStopping: false,
      };

      recorder.ondataavailable = (event) => {
        if (!isUsableChunk(event.data)) return;
        const chunkIndex = segment.chunkCount;
        segment.chunkCount += 1;
        segment.pendingStore = segment.pendingStore.then(() => appendSourceRecordingChunk({
          roomCode,
          uploadId: segment.uploadId,
          index: chunkIndex,
          blob: event.data,
          capturedAt: Date.now(),
        }));
      };
      recorder.onstop = () => {
        segment.stopRecordingSource();
        segment.resolveStopped();
      };
      recorder.onerror = (event) => {
        console.error('[BrowserSourceRecorder] MediaRecorder error', {
          uploadId,
          cameraId: track.cameraId,
          event,
        });
      };

      segmentsRef.current.set(track.cameraId, segment);
      startingCameraIdsRef.current.delete(track.cameraId);
      recorder.start(CHUNK_INTERVAL_MS);

      console.log('[BrowserSourceRecorder] Source recording started', {
        uploadId,
        cameraId: track.cameraId,
        cameraLabel: track.cameraLabel,
        sourceId: track.sourceId,
        pose,
        mimeType: segment.contentType,
        recordingProfile: SOURCE_RECORDING_PROFILE,
        videoBitsPerSecond: SOURCE_RECORDING_PROFILE.videoBitsPerSecond,
        audioBitsPerSecond: segment.hasAudio ? SOURCE_RECORDING_PROFILE.audioBitsPerSecond : null,
        hasAudio: segment.hasAudio,
        sourceDimensions: {
          width: recordingSource.sourceWidth,
          height: recordingSource.sourceHeight,
        },
        canvasDimensions: {
          width: recordingSource.canvasWidth,
          height: recordingSource.canvasHeight,
        },
        settings: track.mediaStreamTrack.getSettings(),
      });
      logVideoDiagnostic('info', 'source-recording-segment-started', {
        uploadId,
        cameraId: track.cameraId,
        cameraLabel: track.cameraLabel,
        sourceId: track.sourceId,
        pose,
        mimeType: segment.contentType,
        recordingProfile: SOURCE_RECORDING_PROFILE,
        sourceDimensions: {
          width: recordingSource.sourceWidth,
          height: recordingSource.sourceHeight,
        },
        canvasDimensions: {
          width: recordingSource.canvasWidth,
          height: recordingSource.canvasHeight,
        },
        hasAudio: segment.hasAudio,
        sourceTrack: {
          id: track.mediaStreamTrack.id,
          label: track.mediaStreamTrack.label || null,
          readyState: track.mediaStreamTrack.readyState,
          settings: track.mediaStreamTrack.getSettings(),
          constraints: track.mediaStreamTrack.getConstraints(),
        },
        activeModel: {
          modelParticipantId: sessionState?.modelParticipantId ?? null,
          modelProfileId: sessionState?.modelProfileId ?? null,
          modelParticipantIds,
          localParticipantProfileId,
        },
      }, diagnosticContext);
    } catch (error) {
      recordingSource?.stop();
      console.error('[BrowserSourceRecorder] Failed to start source recording', {
        cameraId: track.cameraId,
        cameraLabel: track.cameraLabel,
        pose,
        error,
      });
      logVideoDiagnostic('error', 'source-recording-segment-start-failed', {
        cameraId: track.cameraId,
        cameraLabel: track.cameraLabel,
        pose,
        error,
        activeModel: {
          modelParticipantId: sessionState?.modelParticipantId ?? null,
          modelProfileId: sessionState?.modelProfileId ?? null,
          modelParticipantIds,
          localParticipantProfileId,
        },
      }, diagnosticContext);
      startingCameraIdsRef.current.delete(track.cameraId);
    }
  }, [
    diagnosticContext,
    enabled,
    isSignedIn,
    localMicrophoneTrack,
    localParticipantProfileId,
    modelParticipantIds,
    roomCode,
    sessionState?.modelParticipantId,
    sessionState?.modelProfileId,
  ]);

  useEffect(() => {
    logVideoDiagnostic('info', 'source-recording-eligibility', {
      enabled,
      isSignedIn,
      isLocalSelectedModelCamera: isLocalModel,
      activePose,
      cameraSwitching,
      sessionStatus: sessionState?.status ?? null,
      localTrackCount: localModelTracks.length,
      activeModel: {
        modelParticipantId: sessionState?.modelParticipantId ?? null,
        modelProfileId: sessionState?.modelProfileId ?? null,
        modelParticipantIds,
        localParticipantProfileId,
      },
    }, diagnosticContext);
  }, [
    activePose,
    cameraSwitching,
    diagnosticContext,
    enabled,
    isLocalModel,
    isSignedIn,
    localModelTracks.length,
    localParticipantProfileId,
    modelParticipantIds,
    sessionState?.modelParticipantId,
    sessionState?.modelProfileId,
    sessionState?.status,
  ]);

  useEffect(() => {
    if (safeToUpload) {
      void uploadStoredSegments(sessionState?.status === 'ended' ? 'session_end' : 'break');
    }
  }, [safeToUpload, sessionState?.status, uploadStoredSegments]);

  useEffect(() => {
    if (!enabled || !isSignedIn || !isLocalModel || sessionState?.status === 'ended' || !activePose || cameraSwitching) {
      Array.from(segmentsRef.current.keys()).forEach((cameraId) => {
        void stopSegment(cameraId, cameraSwitching ? 'camera-switching' : 'inactive');
      });
      return;
    }

    const currentTracksByCameraId = new Map(localModelTracks.map((track) => [track.cameraId, track]));

    for (const [cameraId, segment] of segmentsRef.current.entries()) {
      const currentTrack = currentTracksByCameraId.get(cameraId);
      if (!currentTrack || segment.pose.key !== activePose.key || segment.sourceId !== currentTrack.sourceId) {
        void stopSegment(cameraId, 'pose-or-camera-source-changed');
      }
    }

    for (const track of localModelTracks) {
      if (!segmentsRef.current.has(track.cameraId)) {
        void startSegment(track, activePose);
      }
    }
  }, [activePose, cameraSwitching, enabled, isLocalModel, isSignedIn, localModelTracks, sessionState?.status, startSegment, stopSegment]);

  useEffect(() => {
    for (const segment of segmentsRef.current.values()) {
      if (poseTimer?.isPaused && segment.recorder.state === 'recording') {
        segment.recorder.pause();
      } else if (!poseTimer?.isPaused && segment.recorder.state === 'paused') {
        segment.recorder.resume();
      }
    }
  }, [poseTimer?.isPaused]);

  useEffect(() => {
    return () => {
      Array.from(segmentsRef.current.keys()).forEach((cameraId) => {
        void stopSegment(cameraId, 'unmount');
      });
    };
  }, [stopSegment]);

  return null;
}
